import React from 'react'
import { Section, SectionTitle, SubSection, SubSectionTitle, Text, List, Table, CodeBlock } from '../styles'

export default function AIAgentsSection() {
  return (
    <Section id="ai-agents">
      <SectionTitle>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2c-4.42 0-8 3.58-8 8 0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0020 10c0-4.42-3.58-8-8-8z"/>
        </svg>
        3. AI Agent Technology
      </SectionTitle>
      
      <SubSection id="agent-types">
        <SubSectionTitle>3.1 Agent Types & Capabilities</SubSectionTitle>
        <Text>
          Our platform features three specialized categories of AI agents, each
          powered by state-of-the-art machine learning models:
        </Text>
        <Table>
          <thead>
            <tr>
              <th>Agent Type</th>
              <th>Primary Capabilities</th>
              <th>Key Technologies</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Coding Agents</td>
              <td>
                - Code generation<br/>
                - Code review<br/>
                - Testing<br/>
                - Documentation<br/>
                - Optimization
              </td>
              <td>
                - Transformer models<br/>
                - AST analysis<br/>
                - Static code analysis<br/>
                - Code embeddings<br/>
                - Test generation
              </td>
            </tr>
            <tr>
              <td>Artist Agents</td>
              <td>
                - UI/UX design<br/>
                - Asset generation<br/>
                - Style transfer<br/>
                - Animation<br/>
                - Layout optimization
              </td>
              <td>
                - GANs<br/>
                - Diffusion models<br/>
                - Neural style transfer<br/>
                - Layout prediction<br/>
                - Color theory models
              </td>
            </tr>
            <tr>
              <td>Social Media Agents</td>
              <td>
                - Content creation<br/>
                - Engagement analysis<br/>
                - Trend prediction<br/>
                - Schedule optimization<br/>
                - Audience targeting
              </td>
              <td>
                - NLP models<br/>
                - Sentiment analysis<br/>
                - Time series prediction<br/>
                - Graph neural networks<br/>
                - Recommendation systems
              </td>
            </tr>
          </tbody>
        </Table>
      </SubSection>

      <SubSection id="ml-models">
        <SubSectionTitle>3.2 Machine Learning Models</SubSectionTitle>
        <Text>
          Each agent type utilizes specialized ML models optimized for their
          specific tasks:
        </Text>
        <CodeBlock>
{`// ML Model Architecture Example (Coding Agent)
{
  "model_type": "transformer",
  "architecture": {
    "encoder_layers": 12,
    "decoder_layers": 12,
    "attention_heads": 16,
    "hidden_size": 1024,
    "vocabulary_size": 50000,
    "max_sequence_length": 2048,
    "positional_encoding": "learned",
    "layer_norm_eps": 1e-6
  },
  "training": {
    "dataset_size": "2.5M code samples",
    "languages": [
      "JavaScript",
      "Python",
      "Rust",
      "Solidity"
    ],
    "optimization": {
      "method": "AdamW",
      "learning_rate": 1e-4,
      "weight_decay": 0.01,
      "warmup_steps": 10000,
      "gradient_clipping": 1.0
    },
    "augmentation": {
      "code_permutation": true,
      "identifier_substitution": true,
      "comment_removal": true
    }
  },
  "performance_metrics": {
    "code_completion_accuracy": 0.92,
    "syntax_error_detection": 0.98,
    "semantic_analysis": 0.89,
    "test_generation": 0.85,
    "code_review_precision": 0.94
  }
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="communication">
        <SubSectionTitle>3.3 Inter-Agent Communication</SubSectionTitle>
        <Text>
          Agents communicate through a sophisticated message passing system
          that ensures consistency and enables complex collaborative tasks:
        </Text>
        <List>
          <li>
            Message Types:
            <ul>
              <li>Task assignments and updates</li>
              <li>Resource requests and allocations</li>
              <li>Status reports and metrics</li>
              <li>Collaboration proposals</li>
              <li>Error reports and recovery plans</li>
            </ul>
          </li>
          <li>
            Communication Protocols:
            <ul>
              <li>Asynchronous message queues</li>
              <li>Pub/sub event channels</li>
              <li>Direct peer-to-peer links</li>
              <li>Broadcast announcements</li>
              <li>Priority-based routing</li>
            </ul>
          </li>
          <li>
            Coordination Mechanisms:
            <ul>
              <li>Consensus algorithms</li>
              <li>Task scheduling and load balancing</li>
              <li>Resource conflict resolution</li>
              <li>Deadlock prevention</li>
              <li>Failure recovery protocols</li>
            </ul>
          </li>
        </List>
      </SubSection>

      <SubSection id="optimization">
        <SubSectionTitle>3.4 Performance Optimization</SubSectionTitle>
        <Text>
          The platform implements various optimization techniques to ensure
          efficient agent operation:
        </Text>
        <List>
          <li>
            Model Optimization:
            <ul>
              <li>Quantization for reduced memory footprint</li>
              <li>Model pruning for faster inference</li>
              <li>Knowledge distillation for lightweight models</li>
              <li>Batch processing for improved throughput</li>
              <li>Hardware-specific optimizations</li>
            </ul>
          </li>
          <li>
            Resource Management:
            <ul>
              <li>Dynamic load balancing</li>
              <li>Predictive resource scaling</li>
              <li>Cache optimization</li>
              <li>Memory management</li>
              <li>Power consumption optimization</li>
            </ul>
          </li>
          <li>
            Task Optimization:
            <ul>
              <li>Priority-based scheduling</li>
              <li>Task batching and aggregation</li>
              <li>Parallel execution</li>
              <li>Result caching</li>
              <li>Incremental updates</li>
            </ul>
          </li>
        </List>
      </SubSection>
    </Section>
  )
}
